import React, { useEffect, useState } from "react";
import {
  UploadCloud, Loader2, FileText, ScrollText, ShoppingBag, HelpCircle, Check, X,
  AlertTriangle, RotateCcw, Eye, Plus, Trash2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, PageHeader, Empty, Spinner, StatusPill } from "@/components/shared";
import { SelectBox } from "@/pages/Clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const TYPE_META = {
  purchase_invoice: { label: "Purchase Invoice", icon: ScrollText, color: "text-amber-400" },
  order_slip: { label: "Order Slip", icon: ShoppingBag, color: "text-sky-400" },
  menu: { label: "Menu / Recipe", icon: FileText, color: "text-violet-400" },
  unknown: { label: "Unknown", icon: HelpCircle, color: "text-slate-400" },
};
const DOC_STATUS = {
  pending_review: "Under Review", approved: "Approved", rejected: "Rejected", reversed: "Inactive",
  processing: "Processing", error: "Error",
};

export default function AIUploadCentre() {
  const { user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [files, setFiles] = useState([]);
  const [hint, setHint] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [batch, setBatch] = useState({ ids: [], total: 0 });

  const load = () => api.get("/ai-docs").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (docs && docs.some((d) => d.status === "processing")) {
      const t = setTimeout(load, 3000);
      return () => clearTimeout(t);
    }
  }, [docs]);

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("doc_type_hint", hint);
      const { data } = await api.post("/ai-docs/upload", fd);
      toast.success(`${data.length} document(s) uploaded — analyzing…`);
      setBatch({ ids: data.map((d) => d.id), total: data.length });
      setFiles([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!docs) return <Spinner />;

  const analyzed = batch.ids.filter((id) => { const d = docs.find((x) => x.id === id); return d && d.status !== "processing"; }).length;
  const showProgress = batch.total > 0 && analyzed < batch.total;

  return (
    <div>
      <PageHeader title="AI Upload Centre" subtitle="Upload invoices, order slips, photos & PDFs — AI extracts the data for your review and approval" />

      <div className="card-surface p-5 mb-6" data-testid="upload-panel">
        <div className="grid sm:grid-cols-[1fr_200px_auto] gap-3 items-end">
          <div>
            <Label className="text-xs text-muted-foreground">Documents (PDF, image/photo, CSV, Excel — max 8MB each)</Label>
            <Input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls,image/*"
              onChange={(e) => setFiles([...e.target.files])} className="mt-1.5" data-testid="upload-file-input" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Document type</Label>
            <div className="mt-1.5">
              <SelectBox value={hint} onChange={setHint} testid="upload-type-hint" options={[
                { value: "auto", label: "Auto-detect" },
                { value: "purchase_invoice", label: "Purchase Invoice" },
                { value: "order_slip", label: "Order Slip" },
              ]} />
            </div>
          </div>
          <Button onClick={upload} disabled={busy || !files.length} data-testid="upload-analyze-btn"
            className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Analyze
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">AI reads each document and prepares a preview. Nothing changes in your inventory or orders until you review and approve.</p>
      </div>

      {showProgress && (
        <div className="card-surface p-4 mb-4" data-testid="upload-progress">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-200 inline-flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin text-amber-300" /> Analyzing documents…</span>
            <span className="font-mono text-amber-300" data-testid="upload-progress-count">{analyzed} of {batch.total} analyzed</span>
          </div>
          <Progress value={(analyzed / batch.total) * 100} className="h-2" />
        </div>
      )}

      {docs.length === 0 ? (
        <Empty icon={UploadCloud} title="No documents yet" hint="Upload an invoice or order slip to get started" />
      ) : (
        <div className="space-y-3">
          {docs.map((d) => {
            const meta = TYPE_META[d.doc_type] || TYPE_META.unknown;
            const Icon = meta.icon;
            return (
              <div key={d.id} className="card-surface p-4 flex items-center gap-4" data-testid={`doc-row-${d.id}`}>
                <Icon className={`h-6 w-6 shrink-0 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white truncate">{d.original_filename}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide border rounded px-1.5 py-0.5 border-border text-slate-400">{meta.label}</span>
                    <StatusPill status={DOC_STATUS[d.status] || d.status} />
                    {d.duplicates?.length > 0 && d.status === "pending_review" && (
                      <span className="text-[10px] font-bold text-rose-400 border border-rose-500/40 rounded px-1.5 py-0.5">DUPLICATE?</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {d.doc_type === "purchase_invoice" && (d.preview?.supplier_name || "Supplier —")}
                    {d.doc_type === "order_slip" && `${d.preview?.platform || "Platform"} · ${d.preview?.order_number || "—"}`}
                    {" · "}by {d.uploaded_by} · conf {Math.round((d.confidence || 0) * 100)}%
                  </p>
                </div>
                {d.status === "processing" ? (
                  <span className="text-xs text-amber-300 inline-flex items-center gap-1.5 px-3"><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</span>
                ) : (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setActive(d)} data-testid={`doc-review-${d.id}`}>
                    <Eye className="h-4 w-4" /> Review <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {active && (
        <ReviewDialog doc={active} isAdmin={user.role === "admin"} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />
      )}
    </div>
  );
}

function ReviewDialog({ doc, isAdmin, onClose, onDone }) {
  const [d, setD] = useState(doc);
  const [brands, setBrands] = useState([]);
  const [ings, setIngs] = useState([]);
  const [menus, setMenus] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ai-docs/" + doc.id).then((r) => setD(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    if (doc.doc_type === "purchase_invoice") api.get("/ingredients").then((r) => setIngs(r.data));
  }, [doc.id]);

  useEffect(() => {
    if (d.doc_type === "order_slip" && d.brand_id) api.get("/menu-items", { params: { brand_id: d.brand_id } }).then((r) => setMenus(r.data));
  }, [d.doc_type, d.brand_id]);

  const editable = d.status === "pending_review";
  const setPreview = (patch) => setD((x) => ({ ...x, preview: { ...x.preview, ...patch } }));
  const setLine = (i, patch, key = "line_items") => setPreview({
    [key]: d.preview[key].map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
  });
  const addLine = () => setPreview({ line_items: [...(d.preview.line_items || []), { extracted_name: "", quantity: 0, unit: "kg", unit_price: 0, is_new: true, approved: true, std_unit: "g" }] });
  const rmLine = (i) => setPreview({ line_items: d.preview.line_items.filter((_, idx) => idx !== i) });

  const brandOpts = brands.map((b) => ({ value: b.id, label: b.name }));
  const ingOpts = [{ value: "__new__", label: "➕ Create new ingredient" }, ...ings.map((i) => ({ value: i.id, label: i.name }))];
  const menuOpts = menus.map((m) => ({ value: m.id, label: m.name }));

  const approve = async (confirmDup = false) => {
    setBusy(true);
    try {
      const brand = brands.find((b) => b.id === d.brand_id);
      const body = {
        doc_type: d.doc_type, preview: d.preview, brand_id: d.brand_id || null,
        client_id: brand ? brand.client_id : d.client_id || null, confirm_duplicate: confirmDup,
      };
      await api.post(`/ai-docs/${d.id}/approve`, body);
      toast.success("Document approved and applied");
      onDone();
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 409) {
        if (window.confirm(detail + "\n\nApprove anyway?")) return approve(true);
      } else toast.error(detail);
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try { await api.post(`/ai-docs/${d.id}/reject`); toast.success("Rejected"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reverse = async () => {
    if (!window.confirm("Reverse this approved document? Inventory, costing and financial records will be restored.")) return;
    setBusy(true);
    try { await api.post(`/ai-docs/${d.id}/reverse`); toast.success("Reversed — records restored"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fileUrl = `${api.defaults.baseURL}/ai-docs/${d.id}/file`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(TYPE_META[d.doc_type] || TYPE_META.unknown).label} — Review
            <StatusPill status={DOC_STATUS[d.status] || d.status} />
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Review the AI-extracted data, correct anything if needed, then approve to apply.</DialogDescription>
        </DialogHeader>

        {d.warnings?.length > 0 && (
          <div className="rounded-lg border border-orange-500/40 bg-orange-500/5 p-3">
            <p className="text-sm font-semibold text-orange-400 flex items-center gap-1.5 mb-1"><AlertTriangle className="h-4 w-4" /> Please check</p>
            <ul className="text-xs text-slate-300 list-disc pl-5 space-y-0.5">{d.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
        {d.duplicates?.length > 0 && (
          <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3">
            <p className="text-sm font-semibold text-rose-400 flex items-center gap-1.5 mb-1"><AlertTriangle className="h-4 w-4" /> Possible duplicate</p>
            <ul className="text-xs text-slate-300 list-disc pl-5">{d.duplicates.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}

        <div className="flex items-center justify-between text-xs">
          {d.storage_path
            ? <a href={fileUrl} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline inline-flex items-center gap-1" data-testid="view-original-btn"><FileText className="h-3.5 w-3.5" /> View original document</a>
            : <span className="text-muted-foreground">Original file not stored</span>}
          <span className="text-muted-foreground">Uploaded by {d.uploaded_by}</span>
        </div>

        {/* INVOICE */}
        {d.doc_type === "purchase_invoice" && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Supplier"><Input disabled={!editable} value={d.preview.supplier_name || ""} onChange={(e) => setPreview({ supplier_name: e.target.value })} data-testid="inv-supplier" /></Field>
              <Field label="Invoice #"><Input disabled={!editable} value={d.preview.invoice_number || ""} onChange={(e) => setPreview({ invoice_number: e.target.value })} data-testid="inv-number" /></Field>
              <Field label="Invoice date"><Input disabled={!editable} type="date" value={d.preview.invoice_date || ""} onChange={(e) => setPreview({ invoice_date: e.target.value })} /></Field>
              <Field label="Total (AED)"><Input disabled={!editable} type="number" value={d.preview.total || 0} onChange={(e) => setPreview({ total: Number(e.target.value) })} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Allocate to brand (optional)"><SelectBox value={d.brand_id || "__none__"} onChange={(v) => setD((x) => ({ ...x, brand_id: v === "__none__" ? "" : v }))} options={[{ value: "__none__", label: "Kitchen master (no allocation)" }, ...brandOpts]} testid="inv-brand" /></Field>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-[24px_1.4fr_0.7fr_0.6fr_0.7fr_1.4fr_28px] gap-2 px-3 py-2 bg-secondary/50 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                <span></span><span>Item</span><span>Qty</span><span>Unit</span><span>Unit price</span><span>Ingredient</span><span></span>
              </div>
              {(d.preview.line_items || []).map((l, i) => (
                <div key={i} className={`grid grid-cols-[24px_1.4fr_0.7fr_0.6fr_0.7fr_1.4fr_28px] gap-2 px-3 py-2 items-center border-t border-border ${l.needs_review ? "bg-orange-500/5" : ""}`} data-testid={`inv-line-${i}`}>
                  <input type="checkbox" disabled={!editable} checked={l.approved !== false} onChange={(e) => setLine(i, { approved: e.target.checked })} className="h-4 w-4" data-testid={`inv-line-approve-${i}`} />
                  <Input disabled={!editable} value={l.extracted_name || ""} onChange={(e) => setLine(i, { extracted_name: e.target.value })} className="h-8 text-sm" />
                  <Input disabled={!editable} type="number" value={l.quantity || 0} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} className="h-8 text-sm" />
                  <Input disabled={!editable} value={l.unit || ""} onChange={(e) => setLine(i, { unit: e.target.value })} className="h-8 text-sm" />
                  <Input disabled={!editable} type="number" value={l.unit_price || 0} onChange={(e) => setLine(i, { unit_price: Number(e.target.value) })} className="h-8 text-sm" />
                  <div className="flex items-center gap-1 min-w-0">
                    <div className="flex-1 min-w-0"><SelectBox value={l.matched_ingredient_id || "__new__"} onChange={(v) => setLine(i, { matched_ingredient_id: v === "__new__" ? null : v, is_new: v === "__new__" })} options={ingOpts} /></div>
                    {l.is_new ? <span className="text-[9px] text-emerald-400 shrink-0">NEW</span> : <span className="text-[9px] text-sky-400 shrink-0">{l.match_score}%</span>}
                  </div>
                  {editable ? <button onClick={() => rmLine(i)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button> : <span />}
                </div>
              ))}
              {editable && <button onClick={addLine} className="w-full text-xs text-amber-400 hover:bg-secondary/40 py-2 flex items-center justify-center gap-1 border-t border-border" data-testid="inv-add-line"><Plus className="h-3.5 w-3.5" /> Add line</button>}
            </div>
          </div>
        )}

        {/* ORDER SLIP */}
        {d.doc_type === "order_slip" && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-4 gap-3">
              <Field label="Platform"><Input disabled={!editable} value={d.preview.platform || ""} onChange={(e) => setPreview({ platform: e.target.value })} data-testid="ord-platform" /></Field>
              <Field label="Order #"><Input disabled={!editable} value={d.preview.order_number || ""} onChange={(e) => setPreview({ order_number: e.target.value })} data-testid="ord-number" /></Field>
              <Field label="Date/time"><Input disabled={!editable} value={d.preview.order_datetime || ""} onChange={(e) => setPreview({ order_datetime: e.target.value })} /></Field>
              <Field label="Total (AED)"><Input disabled={!editable} type="number" value={d.preview.total || 0} onChange={(e) => setPreview({ total: Number(e.target.value) })} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Confirm brand *"><SelectBox value={d.brand_id || "__none__"} onChange={(v) => setD((x) => ({ ...x, brand_id: v === "__none__" ? "" : v }))} options={[{ value: "__none__", label: "— Select brand —" }, ...brandOpts]} testid="ord-brand" /></Field>
              <Field label="Client"><Input disabled value={brands.find((b) => b.id === d.brand_id)?.name ? `Client of ${brands.find((b) => b.id === d.brand_id).name}` : (d.preview.client_hint || "auto from brand")} /></Field>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-[24px_1.6fr_0.6fr_0.7fr_1.6fr] gap-2 px-3 py-2 bg-secondary/50 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                <span></span><span>Item</span><span>Qty</span><span>Price</span><span>Menu item</span>
              </div>
              {(d.preview.items || []).map((l, i) => (
                <div key={i} className={`grid grid-cols-[24px_1.6fr_0.6fr_0.7fr_1.6fr] gap-2 px-3 py-2 items-center border-t border-border ${l.needs_review ? "bg-orange-500/5" : ""}`} data-testid={`ord-line-${i}`}>
                  <input type="checkbox" disabled={!editable} checked={l.approved !== false} onChange={(e) => setLine(i, { approved: e.target.checked }, "items")} className="h-4 w-4" data-testid={`ord-line-approve-${i}`} />
                  <div className="min-w-0"><Input disabled={!editable} value={l.extracted_name || ""} onChange={(e) => setLine(i, { extracted_name: e.target.value }, "items")} className="h-8 text-sm" />{l.modifiers && <p className="text-[10px] text-muted-foreground truncate">{l.modifiers}</p>}</div>
                  <Input disabled={!editable} type="number" value={l.quantity || 1} onChange={(e) => setLine(i, { quantity: Number(e.target.value) }, "items")} className="h-8 text-sm" />
                  <Input disabled={!editable} type="number" value={l.unit_price || 0} onChange={(e) => setLine(i, { unit_price: Number(e.target.value) }, "items")} className="h-8 text-sm" />
                  <div className="flex items-center gap-1 min-w-0">
                    <div className="flex-1 min-w-0"><SelectBox value={l.matched_menu_item_id || "__none__"} onChange={(v) => setLine(i, { matched_menu_item_id: v === "__none__" ? "" : v, needs_review: v === "__none__" }, "items")} options={[{ value: "__none__", label: d.brand_id ? "— Select item —" : "Select brand first" }, ...menuOpts]} /></div>
                    {l.matched_menu_item_id ? <span className="text-[9px] text-sky-400 shrink-0">{l.match_score || ""}</span> : <span className="text-[9px] text-orange-400 shrink-0">pick</span>}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">On approval this order is created under the confirmed brand/client and inventory is deducted at the configured stage.</p>
          </div>
        )}

        {(d.doc_type === "menu" || d.doc_type === "unknown") && (
          <Empty icon={HelpCircle} title={d.doc_type === "menu" ? "Menu document" : "Type not identified"}
            hint={d.doc_type === "menu" ? "Use the AI Import page to import menus and recipes." : "This document can't be applied automatically."} />
        )}

        <DialogFooter className="gap-2">
          {d.status === "approved" && isAdmin && (
            <Button variant="outline" onClick={reverse} disabled={busy} data-testid="reverse-btn" className="gap-1.5 border-rose-500/40 text-rose-400 hover:bg-rose-500/10">
              <RotateCcw className="h-4 w-4" /> Reverse
            </Button>
          )}
          {editable && (
            <>
              <Button variant="outline" onClick={reject} disabled={busy} data-testid="reject-btn" className="gap-1.5"><X className="h-4 w-4" /> Reject</Button>
              {(d.doc_type === "purchase_invoice" || d.doc_type === "order_slip") && (
                <Button onClick={() => approve(false)} disabled={busy} data-testid="approve-btn" className="gap-1.5 bg-primary text-primary-foreground hover:bg-amber-400 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve & Apply
                </Button>
              )}
            </>
          )}
          {!editable && <Button variant="outline" onClick={onClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
